import { where } from "firebase/firestore";
import moment from "moment-timezone";
import React, { useMemo } from "react";
import {
    Button,
    ButtonGroup,
    Card,
    Col,
    Dropdown,
    ListGroup,
    Modal,
    Row,
} from "react-bootstrap";
import { FaDownload, FaImage, FaTrash } from "react-icons/fa";
import { IoBarChartSharp } from "react-icons/io5";
import { Link, useNavigate, useParams } from "react-router";

import {
    requestDeleteFlightTelemetry,
    startNewAnalyticsJob,
    toFolderDownloadItem,
} from "../../api/api.jsx";
import {
    FlightCollectionGroup,
    FlightDocument,
    useFlightDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import DevMenuButton from "../../components/DevMenuButton";
import { DownloadButton } from "../../components/DownloadButton/index.jsx";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import Layout, { Title } from "../../components/Layout/Layout";
import LogDevMenuItem from "../../components/LogDevMenuItem";
import MapButton from "../../components/MapButton";
import StateIcon from "../../components/StateIcon";
import StorageDevMenuItem from "../../components/StorageDevMenuItem";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase";
import urls from "../../urls.jsx";

export default function FlightPage() {
    const commandStates = [
        `scheduled`, // Drone scheduled command
        `started`, // Drone started executing command
        `failed`, // Drone unsuccessfully finished executed command
        `canceled`, // Drone got telled to cancel (abort & delete) command by the backend
        `succeeded`, // Drone successfully finished executed command
    ];

    const [refreshKey, setRefreshKey] = React.useState(0);
    const { organizationId, locationId, flightId } = useParams();
    const [flight] = useFlightDocument(organizationId, locationId, flightId);
    const flightDocumentPath =
        organizationId && locationId && flightId
            ? FlightDocument(organizationId, locationId, flightId).path
            : undefined;
    const { getUserIsAdmin } = useUser();
    const navigate = useNavigate();
    const [commandStateHighlight, setCommandStateHighlight] = React.useState();
    const [showDeleteTelemetryModal, setShowDeleteTelemetryModal] =
        React.useState(false);

    React.useEffect(() => {
        if (
            (organizationId === undefined || locationId === undefined) &&
            flightId !== undefined
        ) {
            FlightCollectionGroup.list([where("id", "==", flightId)]).then(
                (flights) => {
                    if (flights.length > 0) {
                        navigate(
                            urls.flight(
                                flights[0].organizationId,
                                flights[0].locationId,
                                flightId,
                            ),
                            { replace: true },
                        );
                    }
                },
            );
        }
    }, [organizationId, locationId, flightId, navigate]);

    function viewTelemetryReport() {
        const bucketUrl = `gs://corvus-backend.appspot.com/${flightDocumentPath}/dev/telemetry/report/report.html`;

        getFirebaseStorageDownloadUrl(bucketUrl).then((url) => {
            window.open(url, "_blank");
        });
    }

    function viewCustomerTelemetryReport() {
        const bucketUrl = `gs://corvus-backend.appspot.com/${flightDocumentPath}/dev/telemetry/customer_report/report.html`;

        getFirebaseStorageDownloadUrl(bucketUrl).then((url) => {
            window.open(url, "_blank");
        });
    }

    const telemetryFolderDownloadItems = useMemo(
        () => [
            toFolderDownloadItem(
                `organizations/${organizationId}/locations/${locationId}/flights/${flightId}/dev/telemetry`,
            ),
        ],
        [organizationId, locationId, flightId],
    );

    const userTimezone = moment.tz.guess();
    const format = "YYYY-MM-DD HH:mm:ss";
    const timestamp = new Date(flight?.timestamp?.seconds).getTime();
    const dateMoment = moment.unix(timestamp).tz(userTimezone);
    const isTelemetryDeleted = flight?.telemetry === "deleted";

    return (
        <Layout>
            <title>{`Flight by ${flight?.drone?.id} ${dateMoment.format(format)} | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.flight}
                flight={flight ?? null}
            />
            <Modal
                show={showDeleteTelemetryModal}
                onHide={() => setShowDeleteTelemetryModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete telemetry of flight</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the telemetry of this
                    flight? This is not reversible!
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDeleteTelemetryModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            await requestDeleteFlightTelemetry(
                                organizationId,
                                locationId,
                                flightId,
                            );
                            setShowDeleteTelemetryModal(false);
                        }}
                    >
                        <FaTrash
                            variant="danger"
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            <Card className="mb-4">
                <Title
                    title={
                        <>
                            Flight{" "}
                            <TimeDisplay
                                timestamp={flight?.timestamp?.seconds}
                                format="YYYY-MM-DD HH:mm:ss"
                                organizationId={organizationId}
                                locationId={locationId}
                            />
                            {` by ${flight?.drone?.id}`}
                        </>
                    }
                >
                    <div>
                        <MapButton
                            url={`${urls.locationMap(organizationId, locationId)}?flightId=${flightId}`}
                            className="ms-2"
                        />
                        {getUserIsAdmin() && (
                            <ButtonGroup className="ms-2">
                                <DevMenuButton>
                                    <FirestoreDevMenuItem
                                        as={Dropdown.Item}
                                        path={flightDocumentPath}
                                    />
                                    <StorageDevMenuItem
                                        as={Dropdown.Item}
                                        path={flightDocumentPath}
                                    />
                                    <LogDevMenuItem
                                        as={Dropdown.Item}
                                        name="flight"
                                        value={flightId}
                                    />
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                        onClick={() => {
                                            startNewAnalyticsJob(
                                                organizationId,
                                                locationId,
                                                flightId,
                                            );
                                        }}
                                    >
                                        <IoBarChartSharp
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Remake report
                                    </Dropdown.Item>
                                    {/* <Dropdown.Item
                                        onClick={() => setShowDeleteTelemetryModal(true)}>
                                        <FaTrash
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Delete telemetry
                                    </Dropdown.Item> */}
                                </DevMenuButton>
                            </ButtonGroup>
                        )}
                    </div>
                </Title>
                <ListGroup variant="flush">
                    {getUserIsAdmin() && flight?.commands && (
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Commands</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    {commandStates.map((commandState) => {
                                        const commandStateCount =
                                            flight.commands.filter(
                                                (command) =>
                                                    command?.status ===
                                                    commandState,
                                            ).length;
                                        return (
                                            <Button
                                                key={commandState}
                                                variant={
                                                    commandStateCount > 0
                                                        ? "secondary"
                                                        : "outline-secondary"
                                                }
                                                onClick={() => {
                                                    setCommandStateHighlight(
                                                        commandState,
                                                    );
                                                    setRefreshKey(
                                                        (prev) => prev + 1,
                                                    );
                                                }}
                                                size="sm"
                                                className="me-2"
                                                style={
                                                    commandStateCount > 0
                                                        ? {}
                                                        : {
                                                              pointerEvents:
                                                                  "none",
                                                          }
                                                }
                                            >
                                                {commandStateCount}{" "}
                                                {commandState}
                                            </Button>
                                        );
                                    })}
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    )}
                    <ListGroup.Item className="px-4">
                        <Row>
                            <Col xs={12} md={3}>
                                <b>Images</b>
                            </Col>
                            <Col xs={12} md={9}>
                                <h5 className="d-inline">
                                    <Button
                                        variant={
                                            flight?.measurements?.length
                                                ? "secondary"
                                                : "outline-secondary"
                                        }
                                        as={Link}
                                        to={urls.flightMeasurements(
                                            organizationId,
                                            locationId,
                                            flightId,
                                        )}
                                        size="sm"
                                        className="me-2"
                                    >
                                        {flight?.measurementsDeletedAt && (
                                            <FaTrash className="me-1" />
                                        )}
                                        <FaImage className="me-2" />
                                        {flight?.measurements?.length || 0}
                                    </Button>
                                </h5>
                            </Col>
                        </Row>
                    </ListGroup.Item>
                    {flight?.mission?.id && (
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Mission</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    <h5 className="d-inline">
                                        <Button
                                            variant="secondary"
                                            as={Link}
                                            to={urls.locationMission(
                                                organizationId,
                                                locationId,
                                                flight.mission.id,
                                            )}
                                            size="sm"
                                            className="me-2"
                                        >
                                            {flight.mission.id}
                                        </Button>
                                    </h5>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    )}
                    {flight?.drone?.id && (
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Drone</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    <h5 className="d-inline">
                                        <Button
                                            variant="secondary"
                                            as={Link}
                                            to={urls.locationDrone(
                                                organizationId,
                                                locationId,
                                                flight.drone.id,
                                            )}
                                            size="sm"
                                            className="me-2"
                                        >
                                            {flight.drone.id}
                                        </Button>
                                    </h5>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    )}
                    {(getUserIsAdmin() || flight?.telemetryCustomer) && (
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Telemetry</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    <h5 className="d-inline">
                                        {flight?.telemetryCustomer && (
                                            <Button
                                                variant="secondary"
                                                onClick={
                                                    viewCustomerTelemetryReport
                                                }
                                                size="sm"
                                                className="me-2"
                                                disabled={isTelemetryDeleted}
                                            >
                                                {flight.telemetryCustomer ===
                                                "succeeded" ? (
                                                    <IoBarChartSharp
                                                        className="me-1"
                                                        style={{
                                                            marginTop: "-3px",
                                                        }}
                                                    />
                                                ) : (
                                                    <StateIcon
                                                        state={
                                                            flight.telemetryCustomer
                                                        }
                                                        className="me-1"
                                                        color="white"
                                                    />
                                                )}
                                                {getUserIsAdmin()
                                                    ? "Customer report"
                                                    : "Report"}
                                            </Button>
                                        )}

                                        {getUserIsAdmin() && (
                                            <>
                                                <Button
                                                    variant="secondary"
                                                    onClick={
                                                        !isTelemetryDeleted
                                                            ? viewTelemetryReport
                                                            : undefined
                                                    }
                                                    size="sm"
                                                    className="me-2"
                                                    disabled={
                                                        isTelemetryDeleted
                                                    }
                                                >
                                                    {flight?.telemetry ===
                                                    "succeeded" ? (
                                                        <IoBarChartSharp
                                                            className="me-1"
                                                            style={{
                                                                marginTop:
                                                                    "-3px",
                                                            }}
                                                        />
                                                    ) : (
                                                        <StateIcon
                                                            state={
                                                                flight?.telemetry
                                                            }
                                                            className="me-1"
                                                            color="white"
                                                        />
                                                    )}
                                                    {flight?.telemetryCustomer
                                                        ? "Admin report"
                                                        : "Report"}
                                                </Button>
                                                <DownloadButton
                                                    downloadItems={
                                                        telemetryFolderDownloadItems
                                                    }
                                                    filename={`telemetry_${flightId}.zip`}
                                                    buttonText={"Zip"}
                                                    variant="secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    icon={
                                                        <FaDownload className="me-1" />
                                                    }
                                                />
                                            </>
                                        )}
                                    </h5>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    )}
                </ListGroup>
                {getUserIsAdmin() && (
                    <Card.Body className="p-4">
                        <CodeBlock
                            name="Flight"
                            key={`codeblock-${refreshKey}`}
                            code={flight}
                            collectionLimit={
                                commandStateHighlight ? 100000000 : 50
                            }
                            shouldExpandNodeInitially={(keyPath, nodeData) => {
                                if (
                                    typeof nodeData !== "object" ||
                                    nodeData === null
                                )
                                    return false;
                                const path = [...keyPath].reverse().join(".");
                                if (
                                    path === "commands" &&
                                    commandStateHighlight !== undefined
                                )
                                    return true;
                                if (
                                    path.startsWith("commands.[") &&
                                    path.endsWith("]")
                                )
                                    return true;
                                if (commandStateHighlight !== undefined)
                                    return (
                                        nodeData?.status ===
                                        commandStateHighlight
                                    );
                            }}
                        />
                    </Card.Body>
                )}
            </Card>
        </Layout>
    );
}
